import { Link, NavLink, useLocation } from "react-router-dom";
import Brand from "./Brand.jsx";
import Shimmer, { LoadingStatus } from "./Shimmer.jsx";
import { formatUsdcCompact } from "../utils/format.js";
import { navItems, useInvestmentSummary, NavMaskIcon, SummaryRow } from "./navShared.jsx";

/**
 * Persistent left navigation used on all authenticated pages. Mirrors the
 * GILDer Figma sidebar: dark forest-green panel, yellow accent on the
 * active item, plus a "Buy Token" CTA + Investment Summary card pinned
 * to the bottom.
 *
 * Nav icons live as plain SVG files under public/images/. We render them
 * with `mask-image` so the icon shape is taken from the SVG but its
 * fill colour is driven by `currentColor` on the <NavLink>. That way
 * the active item paints in yellow and inactive items paint in white,
 * without having to re-export every SVG with multiple colour variants.
 */
export default function Sidebar() {
    const location = useLocation();
    // Live aggregates for the Investment Summary card. Shared with the
    // mobile MobileDrawer via useInvestmentSummary so the chain-time
    // interest accrual, Standard-only borrow cap, Turbo filter, and
    // shimmer gating live in exactly one place. See navShared.jsx.
    const { tokens, activeCount, netYield, safeVault, showShimmer } =
        useInvestmentSummary();
    const summary = { tokens, activeCount, netYield, safeVault };

    return (
        <aside className="hidden md:flex md:w-52 shrink-0 self-start flex-col bg-[#10281b] text-slate-200 fixed top-0 left-0 h-screen overflow-y-auto p-4 z-30">
            <Link to="/" className="px-1" aria-label="GILDer home">
                <Brand tone="light" />
            </Link>

            <nav className="mt-5 flex-1 space-y-1.5">
                {navItems.map((item) => {
                    const forceActive =
                        item.matchPaths?.includes(location.pathname) ||
                        item.matchPrefixes?.some((p) =>
                            p.endsWith("/")
                                ? location.pathname.startsWith(p)
                                : location.pathname === p
                        );
                    return (
                        <NavLink
                            key={item.label}
                            to={item.to}
                            end={item.to === "/dashboard"}
                            className={({ isActive }) =>
                                `relative flex items-center gap-2.5 rounded-md py-1.5 pl-5 pr-3 text-[12px] font-normal transition-colors ${
                                    isActive || forceActive
                                        ? "bg-[#2d3d22] text-[#FBC206]"
                                        : "text-[#e6c985] hover:text-[#FBC206]"
                                }`
                            }
                        >
                            {({ isActive }) => {
                                const active = isActive || forceActive;
                                return (
                                    <>
                                        {active ? (
                                            <span className="absolute left-0 top-1.5 bottom-1.5 w-[1.5px] rounded-full bg-[#FBC206]" />
                                        ) : null}
                                        <NavMaskIcon src={item.icon} size={12} />
                                        <span>{item.label}</span>
                                    </>
                                );
                            }}
                        </NavLink>
                    );
                })}
            </nav>

            <hr className="mt-3 mb-10 border-t-2 border-white/15" />

            <Link
                to="/marketplace"
                aria-label="Buy $GILD token"
                className="mx-auto inline-flex w-[80%] items-center justify-center gap-2 rounded-md bg-[#FBC206] px-3 py-1.5 text-[13px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
            >
                Buy Token
            </Link>

            <div className="mx-auto mt-3 w-[90%] rounded-lg bg-[#0E2C19] ring-1 ring-white/5 px-3 py-2">
                <div className="whitespace-nowrap text-[9px] font-light uppercase tracking-wider text-white text-center">
                    Investment Summary
                </div>
                <ul className="mt-1.5 space-y-1 text-[10px]" aria-busy={showShimmer || undefined}>
                    {showShimmer && <LoadingStatus as="li" label="Loading investment summary…" />}
                    <SummaryRow
                        label="Tokens"
                        value={
                            showShimmer ? (
                                <Shimmer width="1.5rem" height="0.625rem" tone="light" />
                            ) : (
                                String(summary.tokens)
                            )
                        }
                        valueColor="text-[#FBC206]"
                    />
                    <SummaryRow
                        label="Active Bonds"
                        value={
                            showShimmer ? (
                                <Shimmer width="1.5rem" height="0.625rem" tone="light" />
                            ) : (
                                String(summary.activeCount)
                            )
                        }
                        valueColor="text-[#FBC206]"
                    />
                    <SummaryRow
                        label="Net Yield"
                        value={
                            showShimmer ? (
                                <Shimmer width="2.5rem" height="0.625rem" tone="light" />
                            ) : (
                                <span className="inline-flex items-center gap-1">
                                    {summary.netYield}
                                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22C55E]" />
                                </span>
                            )
                        }
                        valueColor="text-[#FBC206]"
                    />
                    <SummaryRow
                        label="Available to borrow"
                        value={
                            showShimmer ? (
                                <Shimmer width="3rem" height="0.625rem" tone="light" />
                            ) : (
                                `$${formatUsdcCompact(summary.safeVault)}`
                            )
                        }
                        valueColor="text-[#FBC206]"
                    />
                </ul>
            </div>

            {/* Wallet address + Copy + Disconnect lived here until the
                header dropdown (WalletConnect → ConnectedDropdown) took
                ownership of those actions. Keeping them here too would
                duplicate copy/disconnect across two surfaces — confusing
                and out of date with whichever one is iterated on next. */}
        </aside>
    );
}

